import { useCallback, useMemo, useState } from 'react';
import { Copy, Check, Terminal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ApiDocsSectionProps {
  orgId: string;
  collectionId: string;
  activeKey: string;
  /** When true, render only the inner content (no Card/header wrapper) so the
   *  caller can embed this inside its own collapsible section. */
  bare?: boolean;
}

/**
 * Derive the platform-api base URL from the current window origin.
 *
 * Naming convention:
 *   platform-dev.capitol.ai   -> platform-api-development.capitol.ai
 *   platform-hmg.capitol.ai   -> platform-api-hmg.capitol.ai
 *   platform.capitol.ai       -> platform-api.capitol.ai
 *   ey.capitol.ai             -> platform-api-staging.capitol.ai (EY uses staging API)
 *   localhost                  -> platform-api-staging.capitol.ai
 *
 * TODO: Replace hostname→API env mapping with a Vite env var
 * (e.g. VITE_PLATFORM_API_PUBLIC_URL) or a backend-provided URL so new
 * environments (EY regions, etc.) don't silently fall back to staging.
 */
function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://platform-api.capitol.ai';
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://platform-api-staging.capitol.ai';
  }

  // platform-dev -> platform-api-development
  if (hostname === 'platform-dev.capitol.ai') {
    return 'https://platform-api-development.capitol.ai';
  }

  // platform-{env}.capitol.ai -> platform-api-{env}.capitol.ai
  const envMatch = hostname.match(/^platform-(\w+)\.capitol\.ai$/);
  if (envMatch) {
    return `https://platform-api-${envMatch[1]}.capitol.ai`;
  }

  // platform.capitol.ai (prod) -> platform-api.capitol.ai
  if (hostname === 'platform.capitol.ai') {
    return 'https://platform-api.capitol.ai';
  }

  // Fallback for other subdomains (e.g. ey.capitol.ai)
  return 'https://platform-api-staging.capitol.ai';
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard unavailable in insecure context */
      },
    );
  }, [text]);

  return (
    <Button variant='outline' size='sm' onClick={handleCopy}>
      {copied ? (
        <>
          <Check className='size-3.5 mr-1' />
          Copied!
        </>
      ) : (
        <>
          <Copy className='size-3.5 mr-1' />
          Copy
        </>
      )}
    </Button>
  );
}

export function ApiDocsSection({
  orgId,
  collectionId,
  activeKey,
  bare = false,
}: ApiDocsSectionProps) {
  const apiBase = useMemo(() => getApiBaseUrl(), []);
  const key = activeKey || 'YOUR_API_KEY';

  const chatCurl = `curl -N -X POST \\
  ${apiBase}/public/${orgId}/collections/${collectionId}/external-chat \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "What is this collection about?"}'`;

  const searchCurl = `curl -N -X POST \\
  ${apiBase}/public/${orgId}/collections/${collectionId}/external-search \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "search terms"}'`;

  const chatResponse = `data: {"type":"start","id":"msg_abc123"}
data: {"type":"status","text":"Searching collection..."}
data: {"type":"text","text":"The collection covers..."}
data: {"type":"text","text":" topics related to..."}
data: {"type":"done","conversation_id":"conv_abc123"}`;

  const searchResponse = `data: {"type":"results","items":[{"title":"...","snippet":"...","score":0.95}],"total":42,"page":1,"pages":5}
data: {"type":"status","text":"Generating summary..."}
data: {"type":"text","text":"Based on the results..."}
data: {"type":"done"}`;

  const filtersCurl = `curl -X GET \\
  "${apiBase}/public/${orgId}/collections/${collectionId}/external-chat/filters?include_values=true" \\
  -H "Authorization: Bearer ${key}"`;

  const filtersResponse = `{
  "filters": [
    {
      "field": "source_type",
      "label": "Source Type",
      "type": "select",
      "order": 0,
      "cardinality": "single",
      "values": [{ "value": "book", "count": 34774 }]
    }
  ]
}`;

  const facetCurl = `curl -X GET \\
  "${apiBase}/public/${orgId}/collections/${collectionId}/external-chat/facet?field=source_type" \\
  -H "Authorization: Bearer ${key}"`;

  const facetResponse = `{
  "key": "source_type",
  "hits": [
    { "value": "book", "count": 34774 },
    { "value": "pdf", "count": 7133 }
  ]
}`;

  const body = (
    <div className='space-y-6'>
      {/* Chat endpoint */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-medium'>Chat Endpoint</h4>
          <CopyButton text={chatCurl} />
        </div>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{chatCurl}</code>
        </pre>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>message</code>{' '}
            (string, required) — The user&apos;s question
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>
              conversation_id
            </code>{' '}
            (string, optional) — Reuse for multi-turn conversations
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>search_context</code>{' '}
            (string, optional) — Pre-loaded context for dive-deeper mode
          </p>
        </div>
        <details className='group'>
          <summary className='cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground'>
            Response format (SSE)
          </summary>
          <pre className='mt-2 rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{chatResponse}</code>
          </pre>
        </details>
      </div>

      {/* Search endpoint */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-medium'>Search Endpoint</h4>
          <CopyButton text={searchCurl} />
        </div>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{searchCurl}</code>
        </pre>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>query</code> (string,
            required) — Search query
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>page</code> (int,
            optional, default 1) — Page number
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>filters</code>{' '}
            (object, optional) — Key-value filter pairs. Use the{' '}
            <code className='rounded bg-muted px-1 py-0.5'>field</code> values
            returned by the filters endpoint below. Date ranges use two keys,{' '}
            <code className='rounded bg-muted px-1 py-0.5'>
              {'{field}_from'}
            </code>{' '}
            and{' '}
            <code className='rounded bg-muted px-1 py-0.5'>{'{field}_to'}</code>
            .
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>skip_summary</code>{' '}
            (bool, optional, default false) — Return only the results event and
            skip AI summary generation. Use this when paginating so you
            don&apos;t pay for a second summary; keep the existing one on
            screen.
          </p>
        </div>
        <details className='group'>
          <summary className='cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground'>
            Response format (SSE)
          </summary>
          <pre className='mt-2 rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{searchResponse}</code>
          </pre>
        </details>
      </div>

      {/* Filter discovery endpoint */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-medium'>Filter Discovery Endpoint</h4>
          <CopyButton text={filtersCurl} />
        </div>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{filtersCurl}</code>
        </pre>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            Returns the filter controls configured for this collection, so a
            custom UI can render them without hard-coding anything.
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>include_values</code>{' '}
            (bool, optional, default false) — Also return each filter&apos;s
            values from the live index, in the same request
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>limit</code> (int,
            optional, default 100) — Max values per filter
          </p>
          <p>
            Use <code className='rounded bg-muted px-1 py-0.5'>field</code> as
            the key in the search{' '}
            <code className='rounded bg-muted px-1 py-0.5'>filters</code>{' '}
            object. <code className='rounded bg-muted px-1 py-0.5'>type</code>{' '}
            drives the control:{' '}
            <code className='rounded bg-muted px-1 py-0.5'>select</code> /{' '}
            <code className='rounded bg-muted px-1 py-0.5'>multiselect</code> /{' '}
            <code className='rounded bg-muted px-1 py-0.5'>keyword</code> render
            a dropdown,{' '}
            <code className='rounded bg-muted px-1 py-0.5'>text</code> a free
            text input,{' '}
            <code className='rounded bg-muted px-1 py-0.5'>date_range</code> a
            date range.
          </p>
        </div>
        <details className='group'>
          <summary className='cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground'>
            Response format (JSON)
          </summary>
          <pre className='mt-2 rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{filtersResponse}</code>
          </pre>
        </details>
      </div>

      {/* Facet endpoint */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-medium'>Facet Values Endpoint</h4>
          <CopyButton text={facetCurl} />
        </div>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{facetCurl}</code>
        </pre>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            Values for a single filter field. Only fields configured as filters
            on this collection are allowed — anything else returns 403, which
            signals a misconfiguration rather than a transient error.
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>field</code> (string,
            required) — The payload field to facet
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>limit</code> (int,
            optional, default 100) — Max values to return
          </p>
          <p>
            Fetch values at run time rather than baking them into your page:
            they change as content is added or removed.
          </p>
        </div>
        <details className='group'>
          <summary className='cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground'>
            Response format (JSON)
          </summary>
          <pre className='mt-2 rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{facetResponse}</code>
          </pre>
        </details>
      </div>

      {/* Errors */}
      <div className='space-y-2'>
        <h4 className='text-sm font-medium'>Errors</h4>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            Failures return{' '}
            <code className='rounded bg-muted px-1 py-0.5'>
              {'{"detail": "..."}'}
            </code>
            .
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>401</code> — Missing,
            invalid, or revoked API key
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>403</code> — Key not
            valid for this collection, request origin not in the key&apos;s
            allowed domains, or facet on a field that isn&apos;t a configured
            filter
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>422</code> —
            Validation failure (for example a query over its length limit)
          </p>
          <p>
            <code className='rounded bg-muted px-1 py-0.5'>429</code> — Rate
            limit reached. Honour the{' '}
            <code className='rounded bg-muted px-1 py-0.5'>Retry-After</code>{' '}
            response header
          </p>
          <p>
            Two stream-level cases are easy to miss: an{' '}
            <code className='rounded bg-muted px-1 py-0.5'>error</code> event
            can arrive mid-stream after a 200, and the connection can close
            without a <code className='rounded bg-muted px-1 py-0.5'>done</code>{' '}
            event on a proxy timeout. Treat an exhausted stream as completion,
            or your loading state never clears.
          </p>
        </div>
      </div>

      {/* Calling from a browser */}
      <div className='space-y-2'>
        <h4 className='text-sm font-medium'>Calling from a browser</h4>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            These endpoints accept cross-origin browser requests, so you can
            call them from your own site&apos;s JavaScript instead of proxying
            through your backend.
          </p>
          <p>
            The API key is visible to anyone who loads the page — that is
            expected, and the same as the iframe embed. Restrict each key with
            its <strong>Allowed Domains</strong> and <strong>Rate Limit</strong>{' '}
            settings in Manage Keys rather than trying to keep it secret. A
            request from a domain outside the allowlist gets a 403.
          </p>
          <p>
            Consume SSE with{' '}
            <code className='rounded bg-muted px-1 py-0.5'>fetch</code> plus a{' '}
            <code className='rounded bg-muted px-1 py-0.5'>ReadableStream</code>{' '}
            reader.{' '}
            <code className='rounded bg-muted px-1 py-0.5'>EventSource</code>{' '}
            cannot send an{' '}
            <code className='rounded bg-muted px-1 py-0.5'>Authorization</code>{' '}
            header, so it will not work here.
          </p>
          <p>
            If you put your own proxy or CDN in front, disable response
            buffering and set the idle timeout above 15 seconds, or streams will
            be cut mid-response.
          </p>
        </div>
      </div>
    </div>
  );

  // Rendered in both the bare and Card paths, so it lives in one place.
  const intro = (
    <p className='text-xs text-muted-foreground'>
      Call these endpoints with your embed API key to build your own search and
      chat UI, with no iframe. Chat and search stream Server-Sent Events (SSE);
      filter discovery and facets return JSON. For a working reference
      implementation that calls every endpoint below, see the{' '}
      <a
        href='https://github.com/Faction-V/external-chat-rest-demo'
        target='_blank'
        rel='noopener noreferrer'
        className='underline underline-offset-2 hover:text-foreground'
      >
        example React app
      </a>
      .
    </p>
  );

  if (bare) {
    return (
      <>
        {intro}
        {body}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className='pb-3'>
        <div className='flex items-center gap-2'>
          <Terminal className='size-4 text-muted-foreground' />
          <CardTitle className='text-base font-semibold'>API Access</CardTitle>
        </div>
        {intro}
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
